/**
 * RsvpCard.js
 *
 * Lets the guest who claimed the pass say whether they're coming. They can change it any time.
 */

import React, { useState } from "react";
import { Check, PlaneTakeoff, X } from "lucide-react";
import toast from "react-hot-toast";
import { rsvpBoardingPass } from "../helpers/flyApi";

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";

// Heading and text for each answer (null: not answered yet)
const COPY = {
    null    :   { title: "Are you on board?", text: "Let me know if you're coming. You can change your answer any time." },
    true    :   { title: "You're on board. See you at the gate!", text: "Plans changed? You can change your answer any time." },
    false   :   { title: "Sorry you can't make it", text: "If that changes, there's still a seat with your name on it." },
};

/**
 * One answer button: filled when it's the current answer, outlined otherwise
 */
const AnswerButton = ({ selected, accent, disabled, onClick, icon: Icon, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        className="flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-full border-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold tracking-[0.06em] sm:tracking-[0.12em] transition hover:opacity-90 disabled:opacity-60"
        style={selected
            ? { background: accent, borderColor: accent, color: PAPER }
            : { background: "transparent", borderColor: accent, color: accent }}
    >
        <Icon size={16} aria-hidden="true" /> {children}
    </button>
);

/**
 * @param {Object} booking      -   { status: 'owner', rsvp, pass } from the backend
 * @param {Function} onChange   -   Called with the updated booking after saving
 * @returns {JSX.Element}
 */
const RsvpCard = ({ booking, onChange }) => {
    const [saving, setSaving] = useState(false);
    const accent = booking.pass.colour;
    const copy = COPY[booking.rsvp ?? null];

    const answer = async (attending) => {
        if (attending === booking.rsvp) return;
        setSaving(true);
        try {
            onChange(await rsvpBoardingPass(booking.pass.pnr, attending));
            toast.success(attending ? "Seat confirmed" : "Got it, you're not coming");
        } catch (err) {
            toast.error(err.message || "Couldn't save your answer. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="w-full flex gap-3 sm:gap-4 rounded-2xl px-4 py-4 sm:px-5 text-left"
            style={{ background: PAPER, color: INK, borderLeft: `6px solid ${accent}`, boxShadow: "0 6px 18px rgba(27,42,65,0.10)" }}
        >
            <PlaneTakeoff className="shrink-0 mt-0.5" size={22} color={accent} aria-hidden="true" />
            <div className="flex flex-col gap-3 flex-1">
                <div>
                    <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: accent }}>RSVP</p>
                    <p className="font-bold tracking-wide">{copy.title}</p>
                    <p className="text-sm" style={{ color: MUTED }}>{copy.text}</p>
                </div>
                <div className="flex flex-col min-[360px]:flex-row gap-2 sm:gap-3 max-w-md">
                    <AnswerButton selected={booking.rsvp === true} accent={accent} disabled={saving} onClick={() => answer(true)} icon={Check}>
                        YES, I'M IN
                    </AnswerButton>
                    <AnswerButton selected={booking.rsvp === false} accent={accent} disabled={saving} onClick={() => answer(false)} icon={X}>
                        CAN'T MAKE IT
                    </AnswerButton>
                </div>
            </div>
        </div>
    );
};

export default RsvpCard;
